import type { ComponentType, ReactElement, ReactNode } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import {
  hasLoggedOutSelector,
  isLoggedInSelector,
  isPasswordChangeRequiredSelector,
} from "@/features/auth/store/authSlice";
import isEmpty from "lodash/isEmpty";
import NotFound404 from "../components/NotFound404";
import {
  loggedInUserRoleSelector,
  loggedInUserSelector,
} from "@/features/profile/store/userSlice";
import { selectedBrandSelector } from "@/shared/store/brandSlice";
import { logger } from "./loggerHelper";
import { setRedirectPath } from "./localStore";
import { ROUTES } from "./routes/routePaths";

type UserRecord = Record<string, any>;
type BrandRecord = Record<string, any>;

type RootState = {
  auth: {
    hasLoggedOut?: boolean;
    isPasswordChangeRequired?: boolean;
    isUserLoggedIn?: boolean;
  };
  brand: {
    selectedBrand?: BrandRecord | null;
  };
  user: {
    role?: string;
    user?: UserRecord;
  };
};

type ProtectedRouteElementProps = {
  element: ComponentType<{ user: UserRecord }> | ReactElement;
  roles?: string[];
  loggedInUserRole?: string;
  user?: UserRecord;
  isLoggedIn?: boolean;
  hasLoggedOut?: boolean;
  selectedBrand?: BrandRecord | null;
  requireBrand?: boolean;
  isPasswordChangeRequired?: boolean;
};

type ProtectedRouteProps = {
  element: ProtectedRouteElementProps["element"];
  roles?: string[];
  requireBrand?: boolean;
  [key: string]: unknown;
};

type RootSelector<T> = (_state: RootState) => T;

const ProtectedRouteElement = ({
  element: Element,
  roles = [""],
  loggedInUserRole,
  user,
  isLoggedIn,
  hasLoggedOut,
  selectedBrand,
  requireBrand = true,
  isPasswordChangeRequired,
}: ProtectedRouteElementProps) => {
  const location = useLocation();

  // Check if user is logged in
  if (!isEmpty(user) && isLoggedIn) {
    if (isPasswordChangeRequired && location.pathname !== ROUTES.HOME) {
      return <Navigate to={ROUTES.HOME} state={{ from: location }} replace />;
    }

    // Check if brand ID is selected (only if requireBrand is true)
    if (requireBrand && (!selectedBrand || !selectedBrand?.id)) {
      return <Navigate to={ROUTES.ROOT} state={{ from: location }} replace />;
    }

    // Check if user has required role
    if (roles.includes(loggedInUserRole || "")) {
      // Pass user as prop if Element is a component
      if (typeof Element === "function") {
        return <Element user={user || {}} />;
      }
      return Element as ReactNode;
    } else {
      return <NotFound404 />;
    }
  } else {
    // User is not logged in - existing behavior
    if (!hasLoggedOut) {
      const redirectPath = location.pathname + location.search + location.hash;
      const success = setRedirectPath(redirectPath);
      if (!success) {
        logger.error("Failed to store redirect path");
      }
    }
    return <Navigate to={ROUTES.ROOT} state={{ from: location }} replace />;
  }
};

// Updated to use hooks instead of connect for React Router v6 compatibility
const ProtectedRoute = ({
  element,
  roles = [""],
  requireBrand = true,
  ...props
}: ProtectedRouteProps) => {
  const user = useSelector((state: RootState) =>
    (loggedInUserSelector as unknown as RootSelector<UserRecord | undefined>)(
      state
    )
  );
  const isLoggedIn = useSelector((state: RootState) =>
    (isLoggedInSelector as RootSelector<boolean | undefined>)(state)
  );
  const loggedInUserRole = useSelector((state: RootState) =>
    (loggedInUserRoleSelector as unknown as RootSelector<string | undefined>)(
      state
    )
  );
  const hasLoggedOut = useSelector((state: RootState) =>
    (hasLoggedOutSelector as RootSelector<boolean | undefined>)(state)
  );
  const selectedBrand = useSelector((state: RootState) =>
    (
      selectedBrandSelector as unknown as RootSelector<
        BrandRecord | null | undefined
      >
    )(state)
  );
  const isPasswordChangeRequired = useSelector((state: RootState) =>
    (isPasswordChangeRequiredSelector as RootSelector<boolean | undefined>)(
      state
    )
  );

  return (
    <ProtectedRouteElement
      element={element}
      roles={roles}
      loggedInUserRole={loggedInUserRole}
      user={user}
      isLoggedIn={isLoggedIn}
      hasLoggedOut={hasLoggedOut}
      selectedBrand={selectedBrand}
      requireBrand={requireBrand}
      isPasswordChangeRequired={isPasswordChangeRequired}
      {...props}
    />
  );
};

export default ProtectedRoute;
